import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid2,
  Drawer,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import NodeConfigModal from "./menus/NodeConfigModal";
import NodesDynamicForm from "../../forms/NodesDynamicForm";
import AddNodeContext from "../../contexts/AddNodeContext";
import nodeTypesConfig from "../../models/nodeTypesConfig";
import useAutomations from "../../hooks/automationhooks/useAutomations";
import WhatsappSideMenu from "./menus/WhatsappSideMenu";
import { useParams } from "react-router-dom";
import { useThemeContext } from "../../Theme/Theme";
import SMSSideMenu from "./menus/SMSSideMenu";
import EmailSideMenu from "./menus/EmailSideMenu";
import SmsTemplateSelection from "./menus/SMSTemplateSelection";
import WhatsappTemplateSelection from "./menus/WhatsappTemplateSelection";
import EmailTemplateSelection from "./menus/EmailTemplateSelection";
import CRMSideMenu from "./menus/CRMSideMenu";
import SavedQuerySelectionModal from "./menus/SavedQuerySelectionModal";

const AddActionButton = ({ edgeId }) => {
  const COLOR_SURFACE = "#2C2B2D";
  const COLOR_HEADER = "#1E1E1E";
  const COLOR_ACCENT = "#0DA3E2";
  const [optionsModalOpen, setOptionsModalOpen] = useState(false);
  const [templateSel, setTemplateSel] = useState({ open: false, type: null });
  const [preSelectedTemplate, setPreSelectedTemplate] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [customQueryChoiceOpen, setCustomQueryChoiceOpen] = useState(false);
  const [savedQueryModalOpen, setSavedQueryModalOpen] = useState(false);

  const {
    whatsappTemplates,
    emails,
    smsTemplates,
    handleGetSmsTemplates,
    handleGetWhatsappNumbers,
    handleGetWhatsappTemplates,
    handleGetEmails,
    refreshWhatsappTemplates,
    refreshEmailTemplates,
    refreshSmsTemplates,
    handleCreateSavedQuery,
    handleUpdateSavedQuery,
  } = useAutomations();
  const { mode } = useThemeContext();
  const { automationId, brandId } = useParams();

  const [configModal, setConfigModal] = useState({
    open: false,
    content: null,
    isDrawer: false,
  });
  const { addNodeBetweenNodes, nodes: workflowNodes = [] } = useContext(AddNodeContext);
  const conditionNodeTypes = new Set(["customNodeCommand", "conditionNode"]);
  const conditionNodes = nodeTypesConfig.actions.filter((node) =>
    conditionNodeTypes.has(node.type)
  );
  const actionNodes = nodeTypesConfig.actions.filter(
    (node) => !conditionNodeTypes.has(node.type)
  );

  useEffect(() => {
    const currentBrandId = brandId || localStorage.getItem("brandId");
    if (currentBrandId) {
      handleGetWhatsappNumbers(currentBrandId);
      handleGetWhatsappTemplates(currentBrandId);
      handleGetSmsTemplates(currentBrandId);
    }
  }, [brandId]);

  useEffect(() => {
    const currentBrandId = brandId || localStorage.getItem("brandId");
    if (currentBrandId) {
      handleGetEmails(currentBrandId);
    }
  }, [brandId]);

  const handleOptionsButtonClick = (event) => {
    event.stopPropagation();
    setOptionsModalOpen(true);
  };

  const handleOptionsModalClose = () => {
    setOptionsModalOpen(false);
  };

  const handleConfigModalClose = () => {
    setConfigModal({ open: false, content: null, isDrawer: false });
  };

  const handleOptionSelect = (type) => {
    setOptionsModalOpen(false);

    if (["smsMessage", "whatsappMessage", "emailMessage"].includes(type)) {
      setTemplateSel({ open: true, type });
      return;
    }

    const nodeTypeConfig = nodeTypesConfig.actions.find(
      (node) => node.type === type
    );

    if (!nodeTypeConfig) {
      console.error(`Unknown node type: ${type}`);
      return;
    }

    if (nodeTypeConfig.formFields && nodeTypeConfig.formFields.length > 0) {
      const formFields = nodeTypeConfig.formFields.map((field) => {
        return field;
      });

      if (type == "whatsappMessage") {
        setConfigModal({
          open: true,
          isDrawer: false,
          content: (
            <WhatsappSideMenu
              nodes={workflowNodes}
              onSubmit={(formData) => {
                formData = {
                  label: "Whatsapp Message",
                  template: {
                    label: formData.TemplateName,
                    value: formData.TemplateId,
                    placeholderMappings: formData.placeholderMappings,
                  },
                };
                addNodeBetweenNodes(type, formData, edgeId);
              }}
              onClose={handleConfigModalClose}
            />
          ),
        });
      } else if (type == "smsMessage") {
        setConfigModal({
          open: true,
          isDrawer: false,
          content: (
            <SMSSideMenu
              nodes={workflowNodes}
              onSubmit={(formData) => {
                addNodeBetweenNodes(type, formData, edgeId);
              }}
              onClose={handleConfigModalClose}
            />
          ),
        });
      } else if (type === "emailMessage") {
        setConfigModal({
          open: true,
          isDrawer: false,
          content: (
            <EmailSideMenu
              onSubmit={(formData) => {
                addNodeBetweenNodes(type, formData, edgeId);
              }}
              onClose={handleConfigModalClose}
            />
          ),
        });
      } else if (type === "sendToCrm") {
        setConfigModal({
          open: true,
          isDrawer: false,
          content: (
            <CRMSideMenu
              onSubmit={(formData) => {
                addNodeBetweenNodes(type, formData, edgeId);
              }}
              onClose={handleConfigModalClose}
            />
          ),
        });
      } else if (type === "customNodeCommand") {
        setCustomQueryChoiceOpen(true);
      } else {
        setConfigModal({
          open: true,
          isDrawer: false,
          content: (
            <NodesDynamicForm
              title={`${nodeTypeConfig.displayName}`}
              onClose={handleConfigModalClose}
              formFields={formFields}
              onSubmit={(formData) => {
                addNodeBetweenNodes(type, formData, edgeId);
                handleConfigModalClose();
              }}
              setConfigModal={setConfigModal}
              handleCancel={() =>
                setConfigModal({ open: false, isDrawer: false })
              }
            />
          ),
        });
      }
    } else {
      addNodeBetweenNodes(type, {}, edgeId);
    }
  };

  const parseSavedQueryToNodeData = (savedQuery) => {
    let parsed = {};
    try {
      parsed = JSON.parse(savedQuery?.QueryJson || "{}");
    } catch (error) {
      console.error("Failed parsing saved query json:", error);
    }

    const parsedConditions = Array.isArray(parsed?.conditions)
      ? parsed.conditions
      : [];
    const joinConfigs = parsed?.joinConfigs || {};
    const normalizedConditions =
      parsedConditions.length > 0 && Object.keys(joinConfigs).length > 0
        ? [{ ...parsedConditions[0], joinConfigs }, ...parsedConditions.slice(1)]
        : parsedConditions;

    return {
      label: savedQuery?.Name || "Custom Query",
      savedQueryId: savedQuery?.Id,
      savedQueryName: savedQuery?.Name || "Custom Query",
      savedQueryDescription: savedQuery?.Description || "",
      conditions: normalizedConditions,
    };
  };

  const openCustomQueryBuilder = ({
    initialData = {},
    mode = "node",
    savedQuery = null,
  } = {}) => {
    const nodeTypeConfig = nodeTypesConfig.actions.find(
      (node) => node.type === "customNodeCommand"
    );
    const formFields = nodeTypeConfig.formFields.map((field) => field);

    const handleCustomQueryClose = () => {
      handleConfigModalClose();
      if (mode === "libraryEdit") {
        setSavedQueryModalOpen(true);
      }
    };

    setConfigModal({
      open: true,
      isDrawer: true,
      content: (
        <NodesDynamicForm
          title={
            mode === "libraryEdit"
              ? "Edit Saved Query"
              : `Create ${nodeTypeConfig.displayName} Node`
          }
          onClose={handleCustomQueryClose}
          formFields={formFields}
          initialData={{
            ...initialData,
            saveForReuse:
              mode === "libraryEdit" ||
              Boolean(initialData?.saveForReuse || initialData?.savedQueryId),
            queryName: initialData?.queryName || initialData?.savedQueryName || "",
            queryDescription:
              initialData?.queryDescription ||
              initialData?.savedQueryDescription ||
              "",
          }}
          onSaveTemplate={async ({
            savedQueryId,
            name,
            description,
            formData,
          }) => {
            const currentBrandId = Number(
              brandId || localStorage.getItem("brandId")
            );
            const conditions = Array.isArray(formData?.conditions)
              ? formData.conditions
              : [];
            const joinConfigs = conditions[0]?.joinConfigs || {};
            const normalizedConditions = conditions.map((condition) => {
              const next = { ...condition };
              if (next.joinConfigs) delete next.joinConfigs;
              return next;
            });

            const payload = {
              BrandId: currentBrandId,
              Name: (name || "").trim(),
              Description: (description || "").trim() || null,
              Query: {
                conditions: normalizedConditions,
                joinConfigs,
              },
            };

            if (savedQueryId) {
              return handleUpdateSavedQuery(savedQueryId, payload);
            }

            return handleCreateSavedQuery(payload);
          }}
          onSubmit={(formData) => {
            const run = async () => {
              const currentBrandId = Number(
                brandId || localStorage.getItem("brandId")
              );
              const shouldSave =
                mode === "libraryEdit" || Boolean(formData.saveForReuse);
              let savedQueryMeta = null;

              if (shouldSave) {
                const name = (formData.queryName || "").trim();
                if (!name) {
                  alert("Query Name is required when saving for reuse.");
                  return;
                }

                const conditions = Array.isArray(formData.conditions)
                  ? formData.conditions
                  : [];
                const joinConfigs = conditions[0]?.joinConfigs || {};
                const normalizedConditions = conditions.map((condition) => {
                  const next = { ...condition };
                  if (next.joinConfigs) delete next.joinConfigs;
                  return next;
                });

                const payload = {
                  BrandId: currentBrandId,
                  Name: name,
                  Description: (formData.queryDescription || "").trim() || null,
                  Query: {
                    conditions: normalizedConditions,
                    joinConfigs,
                  },
                };

                const savedId =
                  savedQuery?.Id ||
                  formData.savedQueryId ||
                  initialData?.savedQueryId;

                if (savedId) {
                  savedQueryMeta = await handleUpdateSavedQuery(savedId, payload);
                } else {
                  savedQueryMeta = await handleCreateSavedQuery(payload);
                }
              }

              if (mode === "libraryEdit") {
                handleConfigModalClose();
                setSavedQueryModalOpen(true);
                return;
              }

              const cleanData = { ...formData };
              delete cleanData.saveForReuse;
              delete cleanData.queryName;

              if (savedQueryMeta?.Id) {
                cleanData.savedQueryId = savedQueryMeta.Id;
                cleanData.savedQueryName = savedQueryMeta.Name;
                cleanData.savedQueryDescription =
                  savedQueryMeta.Description || "";
                cleanData.label = savedQueryMeta.Name;
              }

              addNodeBetweenNodes("customNodeCommand", cleanData, edgeId);
              handleConfigModalClose();
            };

            run().catch((error) => {
              console.error("Failed to submit custom query:", error);
              alert(error?.message || "Failed to save query.");
            });
          }}
        />
      ),
    });
  };

  const handleTemplateChosen = (tmpl) => {
    const { type } = templateSel;
    setTemplateSel({ open: false, type: null });
    setPreSelectedTemplate(tmpl);

    const openConfig = (node) =>
      setConfigModal({ open: true, isDrawer: false, content: node });

    if (type === "smsMessage") {
      openConfig(
        <SMSSideMenu
          nodes={workflowNodes}
          preSelectedTemplate={tmpl}
          onSubmit={(fd) =>
            addNodeBetweenNodes(
              "smsMessage",
              {
                label: tmpl.TemplateName,
                template: { label: tmpl.TemplateName, value: tmpl.TemplateId },
                ...fd,
              },
              edgeId
            )
          }
          onClose={handleConfigModalClose}
        />
      );
    } else if (type === "whatsappMessage") {
      openConfig(
        <WhatsappSideMenu
          nodes={workflowNodes}
          preSelectedTemplate={tmpl}
          onSubmit={(fd) =>
            addNodeBetweenNodes(
              "whatsappMessage",
              {
                label: tmpl.TemplateName,
                template: {
                  label: tmpl.TemplateName,
                  value: tmpl.TemplateId,
                  placeholderMappings: fd.placeholderMappings,
                },
                ...fd,
              },
              edgeId
            )
          }
          onClose={handleConfigModalClose}
        />
      );
    } else if (type === "emailMessage") {
      openConfig(
        <EmailSideMenu
          preSelectedTemplate={tmpl}
          onSubmit={(fd) =>
            addNodeBetweenNodes(
              "emailMessage",
              { label: tmpl.TemplateName, id: tmpl.Id, ...fd },
              edgeId
            )
          }
          onClose={handleConfigModalClose}
        />
      );
    }
  };

  return (
    <>
      {/* Button with edge-specific styling */}
      <Tooltip title="Add" placement="top">
        <Button
          onClick={handleOptionsButtonClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          variant="contained"
          size="small"
          sx={{
            height: 15,
            width: 15,
            minWidth: 15,
            maxWidth: 15,
            borderRadius: "4px",
            backgroundColor: mode === "dark" ? "grey.800" : "grey.300",
            color: "#aaa",
            padding: 0,
            border: `1px solid ${isHovered ? "#1976d2" : "transparent"}`,
            transition: "border-color 0.2s",
            "&:hover": {
              backgroundColor: mode === "dark" ? "grey.800" : "grey.300",
            },
          }}
        >
          <AddIcon
            sx={{
              fontSize: 10,
              color: "#aaa",
            }}
          />
        </Button>
      </Tooltip>

      {/* Options Modal */}
      <Dialog
        open={optionsModalOpen}
        onClose={handleOptionsModalClose}
        PaperProps={{
          sx: {
            width: "1247px",
            maxWidth: "1247px",
            height: "auto",
            maxHeight: "90vh",
            borderRadius: "3px",
            bgcolor: COLOR_SURFACE,
            color: "#FFFFFF",
            border: "1px solid rgba(173, 180, 189, 0.45)",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.45)",
            overflow: "auto",
          },
        }}
      >
        <DialogTitle
          sx={{
            textAlign: "left",
            bgcolor: COLOR_HEADER,
            color: COLOR_ACCENT,
            fontWeight: 700,
            fontSize: "24px",
            lineHeight: 1.2,
            px: "20px",
            py: "14px",
            borderBottom: "1px solid rgba(173, 180, 189, 0.2)",
          }}
        >
          Add Node
        </DialogTitle>
        <DialogContent
          sx={{
            p: 3,
            pt: "24px !important",
            pb: "24px !important",
            overflowY: "auto",
            bgcolor: COLOR_SURFACE,
            color: "#FFFFFF",
          }}
        >
          <Box sx={{ width: "100%", m: 0, p: "12px" }}>
            <Typography
              sx={{
                color: COLOR_ACCENT,
                fontWeight: 600,
                fontSize: "18px",
                mb: 2,
              }}
            >
              Action Nodes
            </Typography>
            <Grid2 container spacing={3} justifyContent="center" alignItems="center">
              {actionNodes.map((node) => (
                <Grid2
                  item
                  xs={12}
                  sm={4}
                  key={node.type}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => handleOptionSelect(node.type)}
                    sx={{
                      width: 180,
                      height: 180,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                      p: 2,
                      transition: node.isWip ? "none" : "all 0.2s ease-in-out",
                      cursor: node.isWip ? "not-allowed" : "pointer",
                      opacity: node.isWip ? 0.4 : 1,
                      ...(node.isWip
                        ? { pointerEvents: "none" }
                        : {
                            "&:hover": {
                              transform: "scale(1.05)",
                              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                            },
                          }),
                    }}
                  >
                    <node.icon
                      sx={{ fontSize: "40px", mb: 2, color: "text.primary" }}
                    />
                    <div>{node.displayName}</div>
                  </Button>
                </Grid2>
              ))}
            </Grid2>

            <Typography
              sx={{
                color: COLOR_ACCENT,
                fontWeight: 600,
                fontSize: "18px",
                mt: 4,
                mb: 2,
              }}
            >
              Condition Nodes
            </Typography>
            <Grid2 container spacing={3} justifyContent="center" alignItems="center">
              {conditionNodes.map((node) => (
                <Grid2
                  item
                  xs={12}
                  sm={4}
                  key={node.type}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => handleOptionSelect(node.type)}
                    sx={{
                      width: 180,
                      height: 180,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      textAlign: "center",
                      p: 2,
                      transition: node.isWip ? "none" : "all 0.2s ease-in-out",
                      cursor: node.isWip ? "not-allowed" : "pointer",
                      opacity: node.isWip ? 0.4 : 1,
                      ...(node.isWip
                        ? { pointerEvents: "none" }
                        : {
                            "&:hover": {
                              transform: "scale(1.05)",
                              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                            },
                          }),
                    }}
                  >
                    <node.icon
                      sx={{ fontSize: "40px", mb: 2, color: "text.primary" }}
                    />
                    <div>{node.displayName}</div>
                  </Button>
                </Grid2>
              ))}
            </Grid2>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            bgcolor: COLOR_SURFACE,
            px: "20px",
            pb: "24px",
            pt: 0,
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="outlined"
            onClick={handleOptionsModalClose}
            sx={{
              width: "187px",
              height: "50px",
              minWidth: "187px",
              borderRadius: "7px",
              borderColor: COLOR_ACCENT,
              color: "#FFFFFF",
              backgroundColor: "transparent",
              px: "10px",
              py: "10px",
              gap: "10px",
              textTransform: "none",
              "&:hover": {
                bgcolor: "rgba(13, 163, 226, 0.08)",
                borderColor: COLOR_ACCENT,
              },
            }}
          >
            Go Back
          </Button>
        </DialogActions>
      </Dialog>

      {/* Configuration Modal or Drawer */}
      {configModal.open &&
        (configModal.isDrawer ? (
          <Drawer
            open={configModal.open}
            onClose={handleConfigModalClose}
            anchor="left"
            PaperProps={{
              sx: {
                width: "1500px",
                maxWidth: "90vw",
              },
            }}
          >
            {configModal.content}
          </Drawer>
        ) : (
          <NodeConfigModal
            open={configModal.open}
            onClose={handleConfigModalClose}
          >
            {configModal.content}
          </NodeConfigModal>
        ))}

      <Dialog
        open={customQueryChoiceOpen}
        onClose={() => setCustomQueryChoiceOpen(false)}
        PaperProps={{
          sx: {
            width: "520px",
            maxWidth: "95vw",
            bgcolor: COLOR_SURFACE,
            color: "#FFFFFF",
            border: "1px solid rgba(173, 180, 189, 0.45)",
            borderRadius: "3px",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: COLOR_HEADER,
            color: COLOR_ACCENT,
            fontWeight: 700,
            borderBottom: "1px solid rgba(173, 180, 189, 0.2)",
          }}
        >
          Custom Query
        </DialogTitle>
        <DialogContent
          sx={{
            pt: "24px !important",
            pb: "12px !important",
            bgcolor: COLOR_SURFACE,
            color: "#FFFFFF",
          }}
        >
          <Typography sx={{ color: "#FFFFFF", mb: 2 }}>
            Would you like to use an existing saved query or create a new one?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1,
            justifyContent: "space-between",
            bgcolor: COLOR_SURFACE,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => {
              setCustomQueryChoiceOpen(false);
              setSavedQueryModalOpen(true);
            }}
            sx={{
              width: "187px",
              height: "50px",
              minWidth: "187px",
              borderRadius: "7px",
              borderColor: COLOR_ACCENT,
              color: "#FFFFFF",
              textTransform: "none",
              "&:hover": {
                borderColor: COLOR_ACCENT,
                bgcolor: "rgba(13, 163, 226, 0.08)",
              },
            }}
          >
            Use Saved Query
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setCustomQueryChoiceOpen(false);
              openCustomQueryBuilder();
            }}
            sx={{
              width: "187px",
              height: "50px",
              minWidth: "187px",
              borderRadius: "7px",
              borderColor: COLOR_ACCENT,
              color: "#FFFFFF",
              textTransform: "none",
              "&:hover": {
                borderColor: COLOR_ACCENT,
                bgcolor: "rgba(13, 163, 226, 0.08)",
              },
            }}
          >
            Create New Query
          </Button>
        </DialogActions>
      </Dialog>

      <SavedQuerySelectionModal
        open={savedQueryModalOpen}
        brandId={brandId || localStorage.getItem("brandId")}
        onClose={() => setSavedQueryModalOpen(false)}
        onCreateNew={() => {
          setSavedQueryModalOpen(false);
          openCustomQueryBuilder();
        }}
        onUse={(savedQuery) => {
          const nodeData = parseSavedQueryToNodeData(savedQuery);
          addNodeBetweenNodes("customNodeCommand", nodeData, edgeId);
          setSavedQueryModalOpen(false);
        }}
        onEdit={(savedQuery) => {
          setSavedQueryModalOpen(false);
          openCustomQueryBuilder({
            mode: "libraryEdit",
            savedQuery,
            initialData: parseSavedQueryToNodeData(savedQuery),
          });
        }}
      />

      {templateSel.open && templateSel.type === "smsMessage" && (
        <SmsTemplateSelection
          open
          templates={smsTemplates?.data || []}
          setSelected={handleTemplateChosen}
          setClosed={() => setTemplateSel({ open: false, type: null })}
          refreshTemplates={() => refreshSmsTemplates(brandId)}
        />
      )}

      {templateSel.open && templateSel.type === "whatsappMessage" && (
        <WhatsappTemplateSelection
          open
          templates={whatsappTemplates?.data?.Data?.List || []}
          setSelected={handleTemplateChosen}
          setClosed={() => setTemplateSel({ open: false, type: null })}
          refreshTemplates={() => refreshWhatsappTemplates(brandId)}
        />
      )}

      {templateSel.open && templateSel.type === "emailMessage" && (
        <EmailTemplateSelection
          open
          templates={emails?.data || []}
          setSelected={handleTemplateChosen}
          setClosed={() => setTemplateSel({ open: false, type: null })}
          refreshTemplates={() => refreshEmailTemplates(brandId)}
        />
      )}
    </>
  );
};

export default AddActionButton;
